import * as React from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { agents, type AgentSummary } from "@/api/agents";
import { queues, type QueueDoc, type QueueSummary, type QueueTierRow } from "@/api/queues";
import { users } from "@/api/users";
import type { UserSummary } from "@/api/types";
import { useDocInvalidation } from "@/realtime/use-binding";

/**
 * The queries the call center screens share. Keys follow
 * `[noun, accountId, ...id]` and reuse `["users", accountId]` so the
 * account's people are loaded once for the whole console.
 *
 * `queue` is not one of blackhole's document types, so nothing invalidates
 * these caches by itself: the screens refetch after their own writes, and
 * live state comes from the `callcenter.*` family instead.
 */

export function useQueueList(accountId: string | undefined) {
  return useQuery({ queryKey: ["queues", accountId], queryFn: () => queues.list(accountId!), enabled: Boolean(accountId) });
}

export interface QueueRows {
  summaries: QueueSummary[];
  /** The documents, in the order of the listing; a document still loading is absent. */
  docs: Map<string, QueueDoc>;
  loading: boolean;
  error: unknown;
}

/**
 * The queue documents. The listing carries `id` and `name` and nothing
 * else, and the screen shows the strategy, the tier count and the waiting
 * limits, so each document is fetched under the same key the inspector
 * uses. Queue collections are small.
 */
export function useQueueDocs(accountId: string | undefined): QueueRows {
  const list = useQueueList(accountId);
  const summaries = React.useMemo(() => list.data?.items ?? [], [list.data]);
  const ids = React.useMemo(() => summaries.map((q) => q.id), [summaries]);
  const results = useQueries({
    queries: ids.map((id) => ({ queryKey: ["queue", accountId, id], queryFn: () => queues.get(accountId!, id), enabled: Boolean(accountId), staleTime: 60_000 })),
  });
  const stamp = results.map((r) => r.dataUpdatedAt).join(",");
  const docs = React.useMemo(() => {
    const m = new Map<string, QueueDoc>();
    results.forEach((r, i) => {
      if (r.data) m.set(ids[i]!, r.data);
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, stamp]);
  return { summaries, docs, loading: list.isLoading || results.some((r) => r.isLoading), error: list.error ?? results.find((r) => r.error)?.error };
}

/** One queue's tiers, with each agent's user summarised by the server. */
export function useQueueTiers(accountId: string | undefined, queueId: string | undefined) {
  return useQuery({
    queryKey: ["queue-agents", accountId, queueId],
    queryFn: () => queues.agents.list(accountId!, queueId!),
    enabled: Boolean(accountId && queueId),
  });
}

/** Every queue's tiers at once, for the screens that ask "which queues is this person in?". */
export function useAllTiers(accountId: string | undefined, queueIds: string[]): { byQueue: Map<string, QueueTierRow[]>; loading: boolean } {
  const results = useQueries({
    queries: queueIds.map((id) => ({
      queryKey: ["queue-agents", accountId, id],
      queryFn: () => queues.agents.list(accountId!, id),
      enabled: Boolean(accountId),
      staleTime: 30_000,
    })),
  });
  const stamp = results.map((r) => r.dataUpdatedAt).join(",");
  const loading = results.some((r) => r.isLoading);
  return React.useMemo(() => {
    const byQueue = new Map<string, QueueTierRow[]>();
    results.forEach((r, i) => {
      if (r.data) byQueue.set(queueIds[i]!, r.data.items);
    });
    return { byQueue, loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueIds, stamp, loading]);
}

/** Every user of the account with a `call_center` object, with their availability and activity. */
export function useAgentList(accountId: string | undefined, opts: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ["agents", accountId],
    queryFn: () => agents.list(accountId!),
    enabled: Boolean(accountId),
    refetchInterval: opts.refetchInterval,
  });
}

/**
 * The agents whose user is disabled. `GET /agents` says so with
 * `enabled: false`; a deleted user is simply not listed.
 */
export function useDisabledAgents(accountId: string | undefined): ReadonlySet<string> {
  const list = useAgentList(accountId);
  return React.useMemo(() => new Set((list.data?.items ?? []).filter((a) => a.enabled === false).map((a) => a.id)), [list.data]);
}

/** The account's users, for the picker that turns one into an agent. */
export function useAccountUsers(accountId: string | undefined) {
  useDocInvalidation(accountId, "user", [["users", accountId]]);
  const q = useQuery({ queryKey: ["users", accountId], queryFn: () => users.list(accountId!), enabled: Boolean(accountId) });
  const items: UserSummary[] = React.useMemo(() => q.data?.items ?? [], [q.data]);
  return { users: items, loading: q.isLoading };
}

/** Refetch everything a queue or tier write may have touched. */
export function useInvalidateQueues(accountId: string | undefined) {
  const qc = useQueryClient();
  return React.useCallback(
    (queueId?: string) => {
      void qc.invalidateQueries({ queryKey: ["queues", accountId] });
      void qc.invalidateQueries({ queryKey: queueId ? ["queue", accountId, queueId] : ["queue", accountId] });
      void qc.invalidateQueries({ queryKey: queueId ? ["queue-agents", accountId, queueId] : ["queue-agents", accountId] });
      void qc.invalidateQueries({ queryKey: ["agents", accountId] });
    },
    [qc, accountId],
  );
}

/** Refetch what an availability change touched: the agent, their log and every list they are on. */
export function useInvalidateAgent(accountId: string | undefined) {
  const qc = useQueryClient();
  return React.useCallback(
    (userId?: string) => {
      void qc.invalidateQueries({ queryKey: ["agents", accountId] });
      for (const noun of ["agent", "agent-status", "agent-log", "agent-stats"]) void qc.invalidateQueries({ queryKey: userId ? [noun, accountId, userId] : [noun, accountId] });
    },
    [qc, accountId],
  );
}

export type { AgentSummary };
